/**
 * CER portal `<option>` display text for each internal assessment value.
 *
 * WHY THIS EXISTS SEPARATELY FROM `ASSESSMENT_OPTIONS`
 * ----------------------------------------------------
 * `ASSESSMENT_OPTIONS` in `assessment-library.ts` carries the label the
 * *inspector* sees in our own UI. That label is deliberately readable
 * ("Rectification Work", "Medium Non Compliance") and does NOT match the text
 * the CER portal renders in its per-clause Initial Findings Assessment select.
 *
 * Confirmed against the live portal on 2026-08-09 (build 3.9.54.0, battery
 * submission 76663) — three of the labels would not match on a case-insensitive
 * comparison because of hyphenation and a trailing word:
 *
 *   value                   our UI label                  portal option text
 *   ─────────────────────── ───────────────────────────── ──────────────────────────────
 *   minor_non_compliance    Minor Non Compliance          Minor non-compliance
 *   medium_non_compliance   Medium Non Compliance         Medium non-compliance
 *   rectification_work      Rectification Work            Rectification work required
 *
 * Filling the portal with the UI label would silently no-op on the two most
 * common severities. Hence: one explicit map, verified against the DOM.
 *
 * See `docs/CER_PORTAL_BATTERY.md` for the full probe write-up.
 */
/**
 * Internal assessment value → the exact `<option>` text the CER portal renders
 * for a per-clause "Initial Findings Assessment" select.
 *
 * A value mapped to `undefined` is deliberately NOT fillable:
 *   - it has no confirmed portal text (never seen in a live DOM probe), or
 *   - it is an internal-only value that exists to drive our report and QA.
 *
 * Callers must treat `undefined` as "leave the field alone and report it",
 * never as "guess". Writing an unverified string into a compliance form is
 * worse than leaving it blank for the inspector.
 */
export const CER_PORTAL_OPTION_TEXT = Object.freeze({
    /* ── verified live on the battery checklist, 2026-08-09 ─────────────────── */
    compliant: 'Compliant',
    not_applicable: 'Not Applicable',
    not_cited: 'Not Sighted',
    minor_non_compliance: 'Minor non-compliance',
    medium_non_compliance: 'Medium non-compliance',
    rectification_work: 'Rectification work required',
    unsafe: 'Unsafe',
    unsafe_product_recall: 'Unsafe due to product recall',
    /* Verified on the PV checklist, 2026-09-11. */
    non_regulatory: 'Non-regulatory issue',
    inverter_settings: 'Inverter settings',
    /* Internal/unconfirmed values remain unfillable. */
    /**
     * "Unsafe due to third party incident" exists on the portal's OVERALL
     * assessment select, which is `neverFill`. Not confirmed as a per-clause
     * option, so it stays unmapped rather than being guessed from the overall enum.
     */
    unsafe_third_party: undefined,
    /**
     * Internal only — `not_viewed` was added for our own report and QA and has
     * no portal equivalent by design. See the note on `ASSESSMENT_OPTIONS.notViewed`.
     */
    not_viewed: undefined,
});
/**
 * Resolve the portal option text for an internal assessment value.
 * Returns `undefined` when the value must not be written to the portal —
 * either unverified or internal-only. Unknown values also return `undefined`.
 */
export function cerPortalOptionText(value) {
    if (value === undefined)
        return undefined;
    return CER_PORTAL_OPTION_TEXT[value.trim().toLowerCase()];
}
/**
 * The portal's OVERALL "Initial Findings Inspection Assessment" options
 * (`#Initial_Findings_Inspection_Assessment_ID`), verified live 2026-08-09.
 *
 * Recorded for documentation and QA cross-checks ONLY. This select is
 * `neverFill` on `CerPortalPayloadV1` and the extension must never write it.
 *
 * Note there is no "Compliant" or "Passed" option: every value asserts an
 * adverse finding, so a clean inspection means leaving the select blank. That
 * is the mechanical reason the never-fill rule exists — a pass is not
 * expressible here.
 */
export const CER_OVERALL_ASSESSMENT_OPTIONS = Object.freeze([
    'Unsafe',
    'Sub standard',
    'Unsafe due to product recall',
    'Unsafe due to third party incident',
    'Sub standard due to third party incident',
]);
//# sourceMappingURL=cer-portal-option-text.js.map